import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { TableBody, TableCell, TableRow } from '@mui/material';
import { customerDeliveryFileDetailService } from '@services/private/customer-delivery/file-detail.service';
import { ROUTES } from 'constants/routes';

import Button from 'shared/components/widgets/button';
import FieldBox from 'shared/components/widgets/field-box';
import FieldBoxProperty from 'shared/components/widgets/field-box-property';
import { is200xRange } from '@utils/utilities';
import { customerDeliveryFileDeleteService } from '@services/private/customer-delivery/file-delete.service';
import { getCustomerDeliveryFileList } from 'shared/helpers/customer-delivery/get-customer-delivery-file-List';
import { bytesToSize } from 'shared/helpers/bytes-to-size';
import MultipleFieldBox from 'shared/components/widgets/multiple-field-box';
import { isAdmin, isStaff } from '@utils/getRole';
import { copyTextUrl } from 'shared/helpers/copyTextUrl';
import { toast } from 'react-toastify';
type FileData = {
  adminConfigEnv: { cssJsVersion: '' } | null;
  otmDetail: {
    authPwd: string;
    downloadUrl: string;
    expirationYmd: string;
    lfsUploadSt: '10' | '50' | '60' | '90';
    otmSeq: string;
    otmSt: 'allow' | 'deny';
    otmSubject: string;
    regDt: string;
    regName: string;
    regTp: string;
    updDt: string;
    updName: string;
    updTp: string;
    uploadFileList: [
      {
        otmfOriginName: string;
        otmfSize: string;
        regName: string;
        regTp: string;
      },
    ];
  } | null;
};

const CustomerDeliveryFilesDetail = ({
  fileId,
  closeDetailAction,
  serviceRequiredData,
  setServiceRequiredData,
  setReload,
}) => {
  const [isLoading, setLoading] = useState<boolean>(false)
  const [fileData, setFileData] = useState<FileData>({
    adminConfigEnv: null,
    otmDetail: null,
  });

  const [showEditDelete, setShowEditDelete] = useState(isAdmin);

  const getCustomerDeliveryFileDetail = async (fileId) => {
    const { response, statusCode } =
      await customerDeliveryFileDetailService(fileId);

    if (response?.code === '1' && is200xRange(statusCode)) {
      handleFileData([
        { key: 'adminConfigEnv', value: response?.result?.adminConfigEnv },
        { key: 'otmDetail', value: response?.result?.otmDetail },
      ]);

      if (showEditDelete == false && response?.result?.editPermission == 'Y') {
        setShowEditDelete(isStaff);
      }
    }
  };

  const onDeleteClick = async (otmSeq) => {
    if(window.confirm("삭제하시겠습니까?")) {
      await deleteCustomerDeliveryFile(otmSeq);
    }
  };

  const deleteCustomerDeliveryFile = async (otmSeq) => {
    const { response, statusCode } = await customerDeliveryFileDeleteService({
      otmSeq,
    });

    if (response?.code === '1' && is200xRange(statusCode)) {
      closeDetailAction();

      await getCustomerDeliveryFileList(
        serviceRequiredData,
        [],
        setServiceRequiredData,
        [],
      );

      setReload(true);
      /*navigate(ROUTES.CUSTOMER_DELIVERY_FILES.ROOT);*/
    }
  };

  const handleFileData = (params) => {
    setFileData((prevFileData) => {
      const newFileData = { ...prevFileData };
      params.forEach((item) => {
        newFileData[item.key] = item.value;
      });
      return newFileData;
    });
  };

  const onHandlerCopyText = async () => {
    const result = await copyTextUrl({
      type: 'customer',
      url: fileData?.otmDetail?.downloadUrl as string,
      expireDate: fileData?.otmDetail?.expirationYmd as string,
      passwd: fileData?.otmDetail?.authPwd,
      onlyUrl: false
    })
    if (result) {
      toast.success('URL이 복사되었습니다!', {
        toastId: 'toastMsg'
      })
    }
    else {
      toast.error('URL 복사에 실패했습니다. 다시 시도해주세요.', {
        toastId: 'toastMsg'
      })
    }
  }

  useEffect(() => {
    const fetchDetailData = async (id) => {
      setLoading(true)
      await getCustomerDeliveryFileDetail(id);
      setLoading(false)
    }
    fetchDetailData(fileId)
  }, [fileId]);

  return (
    <Box
      padding="54px 40px 40px"
      display="flex"
      flexDirection="column"
      gap="42px"
      height="90vh"
      sx={{ overflowY: 'scroll' }}
    >
      {/* FILE DETAILS */}
      <Typography variant="h6" color="var(--base-black)" textAlign="center">
        파일 상세 정보
      </Typography>
      <Box display="flex" flexDirection="column" gap="30px" position="relative">
        {showEditDelete && (
          <Button
            variant="outlined"
            sx={{
              position: 'absolute',
              right: 0,
              top: '-24px',
              width: '112px',
            }}
            onClick={() =>
              // deleteCustomerDeliveryFile(fileData?.otmDetail?.otmSeq)
              onDeleteClick(fileId)
            }
          >
            삭제하기
          </Button>
        )}
        <Box display="flex" flexDirection="column">
          <Box display="flex" flexDirection="column" gap={5}>
            {/* BASIC INFORMATION */}
            <FieldBox title="기본 정보">
              {/* 1. UPLOAD TITLE */}
              <FieldBoxProperty
                isLoading={isLoading}
                name="업로드 제목"
                type="text"
                value={fileData?.otmDetail?.otmSubject ?? '--'}
              />
              {/* 2. EXPIRATION DATE */}
              <FieldBoxProperty
                isLoading={isLoading}
                name="만료일자"
                type="text"
                value={fileData?.otmDetail?.expirationYmd}
              />
              {/* 3. FILE STATUS */}
              <FieldBoxProperty
                isLoading={isLoading}
                name="파일상태"
                type="text"
                value={
                  fileData?.otmDetail?.otmSt === 'allow'
                    ? '다운로드 가능'
                    : '다운로드 불가'
                }
              />
              {/* 4. UPLOAD STATUS */}
              <FieldBoxProperty
                isLoading={isLoading}
                name="업로드 상태"
                type="text"
                value={
                  (fileData?.otmDetail?.lfsUploadSt === '60' &&
                    '업로드 실패') ||
                  (fileData?.otmDetail?.lfsUploadSt === '50' &&
                    '업로드 완료') ||
                  (fileData?.otmDetail?.lfsUploadSt === '90' &&
                    '만료(파일삭제됨)') ||
                  '--'
                }
              />
              {/* 5. DOWNLOAD URL */}
              <FieldBoxProperty
                isLoading={isLoading}
                name="다운로드 URL"
                value={
                  <Box display="flex" flexDirection="column" alignItems="flex-start" gap={2}>
                    <Typography
                      variant="body1"
                      color="var(--gray-800)"
                      sx={{ wordBreak: 'break-all' }}  // URL을 길이에 맞춰 줄바꿈
                    >
                      {fileData?.otmDetail?.downloadUrl}
                    </Typography>
                    <Button
                      variant="contained"
                      color="secondary"
                      sx={{
                        padding: '14px 6px !important',
                        minHeight: '32px',
                        maxHeight: '32px',
                        fontSize: '14px',
                        lineHeight: '20px',
                        alignItems: 'center',
                      }}
                      onClick={onHandlerCopyText}
                    >
                      문구 + URL복사
                    </Button>
                  </Box>
                }
              />
              {/* 6. PASSWORD */}
              <FieldBoxProperty
                isLoading={isLoading}
                name="패스워드"
                type="text"
                value={fileData?.otmDetail?.authPwd ?? ''}
              />
            </FieldBox>

            {/* REGISTRANT INFORMATION*/}
            <FieldBox title="등록자 정보">
              {/* 1. REGISTRANT TYPE */}
              <FieldBoxProperty
                isLoading={isLoading}
                name="등록자 유형"
                type="text"
                value={
                  fileData?.otmDetail?.regTp === 'admin'
                    ? '관리자'
                    : fileData?.otmDetail?.regTp === 'staff'
                      ? '직원'
                      : '--'
                }
              />
              {/* 2. REGISTRANT NAME */}
              <FieldBoxProperty
                isLoading={isLoading}
                name="등록자"
                type="text"
                value={fileData?.otmDetail?.regName ?? ''}
              />
              {/* 3. REGISTRATION DATE & TIME */}
              <FieldBoxProperty
                isLoading={isLoading}
                name="등록일시"
                type="text"
                value={fileData?.otmDetail?.regDt}
              />
              {/* 4. MODIFICATION TYPE */}
              <FieldBoxProperty
                isLoading={isLoading}
                name="수정 유형"
                type="text"
                value={
                  fileData?.otmDetail?.updTp === 'admin'
                    ? '관리자'
                    : fileData?.otmDetail?.updTp === 'staff'
                      ? '직원'
                      : '--'
                }
              />
              {/* 5. MODIFIER */}
              <FieldBoxProperty
                isLoading={isLoading}
                name="수정자"
                type="text"
                value={fileData?.otmDetail?.updName ?? '--'}
              />
              {/* 6. DATE OF MODIFICATION */}
              <FieldBoxProperty
                isLoading={isLoading}
                name="수정 일시"
                type="text"
                value={fileData?.otmDetail?.updDt}
              />
            </FieldBox>

            {/* UPLOAD FILE INFORMATION*/}

            <MultipleFieldBox title="업로드 파일 정보">
              <TableBody>
                {fileData?.otmDetail?.uploadFileList.map(
                  (uploadItem, index) => {
                    return (
                      <TableRow key={index}>
                        <TableCell>{uploadItem?.otmfOriginName}</TableCell>
                        <TableCell>
                          {bytesToSize(Number(uploadItem?.otmfSize) || 0)}
                        </TableCell>
                        <TableCell>
                          {uploadItem?.regTp === 'admin'
                            ? '관리자'
                            : uploadItem?.regTp === 'staff'
                              ? '직원'
                              : '--'}
                        </TableCell>
                        <TableCell>{uploadItem?.regName}</TableCell>
                      </TableRow>
                    );
                  },
                )}
              </TableBody>
            </MultipleFieldBox>
          </Box>
        </Box>
        <Box display="flex" flexDirection="row" justifyContent="center" gap="14px">
          <Link
            state={{
              fileData: fileData?.otmDetail,
              otmDetail: fileData?.otmDetail,
            }}
            to={{
              pathname: `${ROUTES.CUSTOMER_DELIVERY_FILES.UPDATE_FILE.path}/${fileData.otmDetail?.otmSeq}`,
            }}
          >
            {showEditDelete && (
              <Button
                variant="contained"
                color="primary"
                sx={{ width: '112px' }}
              >
                수정하기
              </Button>
            )}
          </Link>
        </Box>
      </Box>
    </Box>
  );
};

export default CustomerDeliveryFilesDetail;
